// "+ Add Company" modal. Triggered from CompaniesSidebar (+ button)
// and from the empty-state CTA in ChatPane.
//
// Substrate: historyConfirm__backdrop + historyConfirm__modal — same
// liquid-glass shell as the delete-confirm + (later) session-settings.
//
// Fields:
//   - Company name (free-text, becomes slug-id server-side)
//   - Paste curl OR JSON (textarea) — live-parsed; preview shows
//     detected API base + masked bearer; submit blocked on parse error.

import { useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, Upload, X } from "react-feather";
import { useSession } from "../store/session";
import { parseCurlOrJson } from "../lib/curl-parser";

function maskBearer(t: string): string {
  if (t.length <= 10) return "•".repeat(t.length);
  // Compact ellipsis form — doesn't overflow on narrow viewports.
  return `${t.slice(0, 4)}…${t.slice(-4)} (${t.length} chars)`;
}

export default function AddCompanyModal() {
  const closeAddCompany = useSession((s) => s.closeAddCompany);
  const addCompany = useSession((s) => s.addCompany);
  const selectCompany = useSession((s) => s.selectCompany);

  const [name, setName] = useState("");
  const [paste, setPaste] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function onFilePick(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.currentTarget.files?.[0];
    e.currentTarget.value = "";
    if (!f) return;
    if (f.size > 1024 * 1024) {
      setError("File too large (max 1MB).");
      return;
    }
    try {
      const text = await f.text();
      setPaste(text);
      setError(null);
    } catch (err: any) {
      setError(`Couldn't read file: ${err.message}`);
    }
  }

  useEffect(() => {
    nameRef.current?.focus();
  }, []);
  // Esc closes.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") closeAddCompany();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [closeAddCompany]);

  const parsed = useMemo(() => (paste.trim() ? parseCurlOrJson(paste) : null), [paste]);
  const parseError = parsed && !parsed.ok ? parsed.error : null;
  const parseOk = !!(parsed && parsed.ok);

  const canSubmit = name.trim().length > 0 && parseOk && !submitting;

  async function submit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await addCompany(name.trim(), paste);
      closeAddCompany();
      // Auto-select the new company.
      const id = name
        .trim()
        .toLowerCase()
        .normalize("NFKD")
        .replace(/[^\w\s-]/g, "")
        .replace(/\s+/g, "-")
        .slice(0, 40);
      await selectCompany(id);
    } catch (err: any) {
      setError(err.message ?? "Failed to add company.");
      setSubmitting(false);
    }
  }

  return (
    <div
      className="historyConfirm__backdrop"
      onClick={closeAddCompany}
      role="dialog"
      aria-modal="true"
      aria-labelledby="addcompany-title"
    >
      <div
        className="historyConfirm__modal historyConfirm__modal--wide"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="modal__close"
          onClick={closeAddCompany}
          aria-label="Close"
        >
          <X width={16} height={16} />
        </button>

        <h3 className="historyConfirm__title" id="addcompany-title">
          Add a Company
        </h3>
        <p className="historyConfirm__body">
          Paste the curl or JSON from your Reputifly Cloud account.
          The bearer is stored server-side and never echoed back.
        </p>

        <div className="modalField">
          <label htmlFor="addcompany-name" className="modalField__label">
            Company Name
          </label>
          <input
            ref={nameRef}
            id="addcompany-name"
            type="text"
            className="modalField__input"
            placeholder="e.g. IMI Creative"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            maxLength={80}
            disabled={submitting}
          />
        </div>

        <div className="modalField">
          <div className="modalField__labelRow">
            <label htmlFor="addcompany-paste" className="modalField__label">
              Paste Curl or JSON
            </label>
            <button
              type="button"
              className="modalField__uploadBtn"
              onClick={() => fileRef.current?.click()}
              disabled={submitting}
            >
              <Upload width={12} height={12} />
              <span>Upload .json</span>
            </button>
            <input
              ref={fileRef}
              type="file"
              accept=".json,.txt,application/json,text/plain"
              style={{ display: "none" }}
              onChange={onFilePick}
            />
          </div>
          <textarea
            id="addcompany-paste"
            className="modalField__textarea"
            placeholder={`curl -X GET "https://imi.reputifly.cloud/wp-json/reputifly/v1/agent/profile" \\\n  -H "Authorization: Bearer abc...xyz"\n\n— or —\n\n{ "api_base": "https://imi.reputifly.cloud/wp-json/reputifly/v1/agent",\n  "agent_key": "abc...xyz" }`}
            value={paste}
            onChange={(e) => setPaste(e.currentTarget.value)}
            rows={7}
            disabled={submitting}
            spellCheck={false}
            autoComplete="off"
          />
        </div>

        {/* Parse preview — icon dropped per Julian's feedback; the
            green border already conveys "ok" status, the icon was noise. */}
        {paste.trim() && parsed && (
          parsed.ok ? (
            <div className="modalPreview modalPreview--ok">
              <div className="modalPreview__lines">
                <code className="modalPreview__line">{parsed.value!.apiBase}</code>
                <code className="modalPreview__line modalPreview__line--muted">
                  Bearer {maskBearer(parsed.value!.bearer)}
                </code>
              </div>
            </div>
          ) : (
            <div className="modalPreview modalPreview--err">
              <AlertTriangle width={14} height={14} />
              <span>{parseError}</span>
            </div>
          )
        )}

        {error && (
          <div className="modalPreview modalPreview--err">
            <AlertTriangle width={14} height={14} />
            <span>{error}</span>
          </div>
        )}

        <div className="historyConfirm__buttons">
          <button
            type="button"
            className="historyConfirm__btn historyConfirm__btn--cancel"
            onClick={closeAddCompany}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="historyConfirm__btn historyConfirm__btn--primary"
            onClick={submit}
            disabled={!canSubmit}
          >
            {submitting ? (
              <>
                <span>Adding…</span>
                <span className="spinner" />
              </>
            ) : (
              <span>Add company</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
